import { Module } from 'vuex';

import { ShiftCMSState } from '@/store';
import { EventItem } from '@/models/event-item.model';
import { EventSource } from '@/models/event-source.model';
import eventsService from '@/services/events.service';
import eventSourceService from '@/services/event-source.service';
import { FilterData } from '@/services/users.service';

export interface EventsState {
  items: EventItem[];
  sources: EventSource[];
}

export enum EventsMutations {
  SetEventItems = 'ev/SetEventItems',
  SetEventSources = 'ev/SetEventSources',
}

export enum EventsActions {
  GetEventItem = 'ev/GetEventItem',
  GetEventItems = 'ev/GetEventItems',
  GetEventItemsPaginated = 'ev/GetEventItemsPaginated',
  AddEventItem = 'ev/AddEventItem',
  UpdateEventItem = 'ev/UpdateEventItem',
  UpdateEventsOrderItems = 'ev/UpdateEventsOrderItems',
  DeleteEventItem = 'ev/DeleteEventItem',
  PublishEventItem = 'ev/PublishEventItem',
  DismissEventItem = 'ev/DismissEventItem',
  GetEventSource = 'ev/GetEventSource',
  GetEventSources = 'ev/GetEventSources',
  AddEventSource = 'ev/AddEventSource',
  UpdateEventSource = 'ev/UpdateEventSource',
  DeleteEventSource = 'ev/DeleteEventSource',
}

const sn = (str: EventsActions | EventsMutations): string =>
  str.replace(/ev\//g, '');

export const eventsStore: Module<EventsState, ShiftCMSState> = {
  namespaced: true,
  state: {
    items: [],
    sources: [],
  },
  mutations: {
    [sn(EventsMutations.SetEventItems)](state, items: EventItem[]) {
      state.items = items;
    },
    [sn(EventsMutations.SetEventSources)](state, sources: EventSource[]) {
      state.sources = sources;
    },
  },
  actions: {
    async [sn(EventsActions.GetEventItem)](_, id) {
      const res = await eventsService.get(id);
      if (res.status) {
        return res.data;
      }
    },
    async [sn(EventsActions.GetEventItems)]({ commit }) {
      const res = await eventsService.getList();
      if (res.status) {
        commit(sn(EventsMutations.SetEventItems), res.data);
      }
    },
    async [sn(EventsActions.GetEventItemsPaginated)](
      { commit },
      filters: FilterData,
    ) {
      const res = await eventsService.getListPaginated(filters);
      if (res.status) {
        commit(sn(EventsMutations.SetEventItems), res.data.items);
      }
    },
    async [sn(EventsActions.AddEventItem)]({ dispatch }, item: EventItem) {
      const res = await eventsService.create(item);
      if (res.status) {
        dispatch(sn(EventsActions.GetEventItems));
      }

      return res;
    },
    async [sn(EventsActions.UpdateEventItem)]({ dispatch }, item: EventItem) {
      const res = await eventsService.update(item);
      if (res.status) {
        dispatch(sn(EventsActions.GetEventItems));
      }
      return res;
    },
    async [sn(EventsActions.PublishEventItem)]({ dispatch }, item: EventItem) {
      const res = await eventsService.publish(item.id, item.published);
      if (res.status) {
        dispatch(sn(EventsActions.GetEventItems));
      }
      return res.status;
    },
    async [sn(EventsActions.DismissEventItem)]({ dispatch }, item: EventItem) {
      const res = await eventsService.dismiss(item.id, item.dismissed);
      if (res.status) {
        dispatch(sn(EventsActions.GetEventItems));
      }
      return res.status;
    },
    async [sn(EventsActions.DeleteEventItem)]({ dispatch }, id: number) {
      const res = await eventsService.delete(id);

      if (res.status) {
        dispatch(sn(EventsActions.GetEventItems));
      }
    },
    async [sn(EventsActions.GetEventSource)](_, id) {
      const res = await eventSourceService.get(id);
      if (res.status) {
        return res.data;
      }
    },
    async [sn(EventsActions.GetEventSources)]({ commit }) {
      const res = await eventSourceService.getList();

      if (res.status) {
        commit(sn(EventsMutations.SetEventSources), res.data);
      }
    },
    async [sn(EventsActions.AddEventSource)](
      { dispatch },
      source: EventSource,
    ) {
      const res = await eventSourceService.create(source);
      if (res.status) {
        dispatch(sn(EventsActions.GetEventSources));
      }

      return res.status;
    },
    async [sn(EventsActions.UpdateEventSource)](
      { dispatch },
      source: EventSource,
    ) {
      const res = await eventSourceService.update(source);
      if (res.status) {
        dispatch(sn(EventsActions.GetEventSources));
      }
      return res.status;
    },
    async [sn(EventsActions.DeleteEventSource)]({ dispatch }, id: number) {
      const res = await eventSourceService.delete(id);

      if (res.status) {
        dispatch(sn(EventsActions.GetEventSources));
      }
    },
  },
};
